import { osnovaApi } from '../base';
import type {
  PtoRdImport,
  PtoRdImportDetail,
  PtoRdImportDetailWithFiles,
  PtoRdImportFile,
  PtoRdImportPlan,
  PtoRdQueue,
} from '../types';

// ОСНОВА.Инженер — загрузка РД архивом и очередь обработки РД
// (docs/pto-rd-import-spec.md). Архив распаковывается в браузере, файлы
// уходят по одному; раскладку (титулы → комплекты → файлы) инженер правит и
// применяет. Статус пакета и очередь опрашиваются (pollingInterval) — поэтому
// карточка пакета лёгкая (без списка файлов), а список файлов (`?files=1`)
// запрашивается отдельно и только для дозагрузки.

export const ptoRdImportApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Пакеты (свои; сквозные роли — все). `plan` и `summary` здесь пустые. */
    listPtoRdImports: b.query<{ items: PtoRdImport[] }, void>({
      query: () => '/agents/pto/rd-imports',
      providesTags: ['PtoRdImport'],
    }),
    /** Карточка пакета: пакет, файлы по состояниям, индексация, место в очереди. */
    getPtoRdImport: b.query<PtoRdImportDetail, string>({
      query: (id) => `/agents/pto/rd-imports/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PtoRdImport', id }],
    }),
    /**
     * Карточка со списком файлов — для дозагрузки (пропустить уже загруженные).
     * Без тегов и кэша: запрашивается разово перед отправкой, а пакет бывает в
     * сотни файлов — перечитывать его на каждую инвалидацию незачем.
     */
    getPtoRdImportFiles: b.query<PtoRdImportDetailWithFiles, string>({
      query: (id) => `/agents/pto/rd-imports/${id}?files=1`,
      keepUnusedDataFor: 0,
    }),
    createPtoRdImport: b.mutation<
      { import: PtoRdImport },
      { source_name: string; project_id: string }
    >({
      query: (body) => ({ url: '/agents/pto/rd-imports', method: 'POST', body }),
      invalidatesTags: ['PtoRdImport'],
    }),
    /**
     * Файл пакета — multipart: `path` (путь в архиве) + файл. Пакет не в
     * `uploading` → 409, пустой файл → 400, больше 200 МБ → 413.
     */
    uploadPtoRdImportFile: b.mutation<
      { file?: PtoRdImportFile; skipped?: boolean },
      { id: string; form: FormData }
    >({
      query: ({ id, form }) => ({
        url: `/agents/pto/rd-imports/${id}/files`,
        method: 'POST',
        body: form,
      }),
    }),
    recognizePtoRdImport: b.mutation<{ import: PtoRdImport }, string>({
      query: (id) => ({ url: `/agents/pto/rd-imports/${id}/recognize`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => ['PtoRdImport', { type: 'PtoRdImport', id }],
    }),
    /** Правки раскладки → сверенная с реестром раскладка (ключи комплектов
     *  могут измениться — после сохранения брать раскладку из ответа). */
    updatePtoRdImportPlan: b.mutation<
      { plan: PtoRdImportPlan },
      { id: string; plan: PtoRdImportPlan }
    >({
      query: ({ id, plan }) => ({
        url: `/agents/pto/rd-imports/${id}/plan`,
        method: 'POST',
        body: { plan },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'PtoRdImport', id }],
    }),
    /** Применение в очередь: из `review`, а из `failed` — если есть раскладка. */
    applyPtoRdImport: b.mutation<{ import: PtoRdImport }, string>({
      query: (id) => ({ url: `/agents/pto/rd-imports/${id}/apply`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => ['PtoRdImport', { type: 'PtoRdImport', id }],
    }),
    deletePtoRdImport: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/rd-imports/${id}/delete`, method: 'POST' }),
      invalidatesTags: ['PtoRdImport'],
    }),
    getPtoRdQueue: b.query<PtoRdQueue, void>({
      query: () => '/agents/pto/rd-queue',
    }),
  }),
});

export const {
  useListPtoRdImportsQuery,
  useGetPtoRdImportQuery,
  useLazyGetPtoRdImportFilesQuery,
  useCreatePtoRdImportMutation,
  useUploadPtoRdImportFileMutation,
  useRecognizePtoRdImportMutation,
  useUpdatePtoRdImportPlanMutation,
  useApplyPtoRdImportMutation,
  useDeletePtoRdImportMutation,
  useGetPtoRdQueueQuery,
} = ptoRdImportApi;
